import { Button, message, Input, DatePicker, Select, Space } from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import CustomTable from "../../components/Table/CustomTable";
import { getUserRole } from "../../utils/auth";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

export default function PurchaseReport() {
  const [purchases, setPurchases] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    storeName: null,
    search: "",
    fromDate: null,
    toDate: null,
    financialYear: null, // New: Financial year filter
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    console.log(role);
    getStore(); // Fetch stores on mount
  }, []);

  // Generate financial years starting from 2024-2025 to current financial year (April-based)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024; // Start from 2024
    let endYear = startYear + 1;

    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }

    return financialYears; // Return in ascending order (oldest to newest)
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value || null, // Ensure null is set if empty value
    }));
  };

  const getPurchases = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { storeName, search, fromDate, toDate, financialYear } = filters; // Added financialYear
      const response = await API.get("/purchase/paginate", {
        params: {
          page,
          limit: pageSize,
          storeName,
          search,
          fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
          toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
          financialYear: financialYear || null, // Pass financial year to API
        },
      });

      const purchasesData = Array.isArray(response?.data?.purchases)
        ? response.data.purchases
        : [];
      setPurchases(purchasesData);

      setPaginationInfo({
        current: page,
        pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching purchases:", err);
      message.error("Failed to load purchases.");
    } finally {
      setLoading(false);
    }
  };

  const getStore = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
      // Set initial storeName based on userData
      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();

      if (userRoleValue.role !== 'admin' && userStoreId) {
        const matchingStore = response.data.find((store) => store._id === userStoreId);
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
      message.error("Failed to load stores.");
    }
  };

  useEffect(() => {
    getPurchases(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize]);

  const handleDownload = async (data) => {
    try {
      const result = await API.get(`/purchase/generate-invoice/${data?._id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${data?.invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Inv. No.",
      render: (_, purchase) => purchase?.invoiceId,
      key: "invoiceId",
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange("search", e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Inv. Date",
      key: "orderDate",
      render: (_, purchase) => dayjs(purchase?.orderDate).format("DD-MM-YYYY"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={
              selectedKeys && selectedKeys.length > 0
                ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])]
                : []
            }
            onChange={(dates) =>
              setSelectedKeys(
                dates
                  ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")]
                  : []
              )
            }
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("fromDate", selectedKeys[0]);
                  handleSearchChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("fromDate", null);
                handleSearchChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: "Inv. Amount (₹)",
      key: "totalAmount",
      render: (_, purchase) =>
        `₹ ${new Intl.NumberFormat().format(purchase?.totalAmount)}`,
    },
    {
      title: "Paid Amount (₹)",
      key: "paidAmount",
      render: (_, purchase) =>
        `₹ ${new Intl.NumberFormat().format(purchase?.paidAmount)}`,
    },
    {
      title: "Balance Amount (₹)",
      key: "balanceAmount",
      render: (_, purchase) =>
        `₹ ${new Intl.NumberFormat().format(purchase?.balanceAmount)}`,
    },
    {
      title: "Supplier",
      render: (_, purchase) => purchase?.supplierId?.name,
      filterDropdown: () => (
        <Input
          placeholder="Search Supplier"
          onChange={(e) => handleSearchChange("search", e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      key: "supplierName",
    },
    {
      title: "Store",
      key: "storeName",
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", color: '#1591EA', fontSize: "12px" }}>
            {sale?.storeId?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        if (status === "pending") color = "yellow";
        else if (status === "completed") color = "green";
        else if (status === "cancelled") color = "red";
        return (
          <span style={{ color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, purchase) => (
        <div>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(purchase)}
            style={{ marginRight: "8px", color: "green" }}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <div
        style={{
          fontWeight: "bold",
          fontSize: "20px",
          textAlign: "center",
          marginBottom: "10px",
          color: "#1890ff",
        }}
      >
        Purchase Report
      </div>
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 10px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          marginBottom: "10px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "500" }}>
          Filter by Store and Financial Year:
        </div>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="Select Store"
            value={filters.storeName}
            onChange={(value) => handleSearchChange("storeName", value)}
            allowClear
                        disabled={userRole!='admin'}

          >
            {stores.map((store) => (
              <Option key={store._id} value={store.name}>
                {store.name}
              </Option>
            ))}
          </Select>
          <Select
            style={{ width: 200 }}
            placeholder="Select Financial Year"
            value={filters.financialYear}
            onChange={(value) => handleSearchChange("financialYear", value)}
            allowClear
          >
            {generateFinancialYears().map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Space>
      </Space>
      <CustomTable
        columns={columns}
        dataSource={purchases}
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          });
        }}
        loading={loading}
        style={{ padding: "1 1px", width: "100%" }}
        total={paginationInfo.total}
      />
    </div>
  );
}